import React, { useState, useRef, useEffect } from 'react';
import { AudioRecorder } from '../utils/audioRecorder';
import { useToast } from './Toast';
import { transcriptionService } from '../services/transcriptionService';

/**
 * Упрощенный компонент записи аудио (без сохранения в S3)
 * Записывает аудио → отправляет на транскрибацию → вставляет текст в notes → удаляет аудио
 */
export function SimpleVoiceRecorder({ 
  onTranscriptionComplete,
  disabled = false 
}) {
  const toast = useToast();
  
  const [state, setState] = useState('idle'); // idle, recording, transcribing
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioMimeType, setAudioMimeType] = useState(null);
  
  const recorderRef = useRef(null);
  const timerRef = useRef(null);
  
  // Cleanup
  useEffect(() => {
    return () => {
      if (recorderRef.current) {
        recorderRef.current.cancelRecording();
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);
  
  /**
   * Начать запись
   */
  const handleStartRecording = async () => {
    // console.log('[SimpleVoiceRecorder] Начало записи');
    
    try {
      recorderRef.current = new AudioRecorder();
      await recorderRef.current.startRecording();
      
      setState('recording');
      setRecordingDuration(0);
      
      // Таймер с автоостановкой после 60 сек
      timerRef.current = setInterval(() => {
        const duration = recorderRef.current.getCurrentDuration();
        setRecordingDuration(duration);
        
        if (duration >= 60) {
          // console.log('[SimpleVoiceRecorder] Автоостановка 60 сек');
          // ВАЖНО: Сначала очищаем таймер, чтобы избежать повторных вызовов!
          clearInterval(timerRef.current);
          timerRef.current = null;
          // Теперь останавливаем запись
          handleStopRecording();
        }
      }, 100);
      
    } catch (error) {
      console.error('[SimpleVoiceRecorder] Ошибка записи:', error);
      
      if (error.name === 'NotAllowedError') {
        toast.error('Нет доступа к микрофону');
      } else {
        toast.error('Ошибка записи аудио');
      }
      
      setState('idle');
    }
  };
  
  /**
   * Остановить запись
   */
  const handleStopRecording = async () => {
    try {
      // Очищаем таймер сразу
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      // Проверяем что recorder существует и записывает
      if (!recorderRef.current || !recorderRef.current.isRecording()) {
        // console.warn('[SimpleVoiceRecorder] Recorder не записывает, пропускаем stopRecording');
        setState('idle');
        return;
      }
      
      const result = await recorderRef.current.stopRecording();
      
      setAudioBlob(result.blob);
      setAudioMimeType(result.mimeType);
      setState('idle');
      
      toast.success(`Записано ${result.duration} сек`);
      
    } catch (error) {
      console.error('[SimpleVoiceRecorder] Ошибка остановки:', error);
      toast.error('Ошибка сохранения записи');
      setState('idle');
    }
  };
  
  /**
   * Транскрибация аудио
   */
  const handleTranscribe = async () => {
    if (!audioBlob) {
      toast.error('Нет аудио для транскрибации');
      return;
    }
    
    try {
      setState('transcribing');
      // console.log('[SimpleVoiceRecorder] Отправка на транскрибацию...');
      
      const transcribedText = await transcriptionService.transcribeAudio(audioBlob, audioMimeType);
      
      // console.log('[SimpleVoiceRecorder] Транскрибация успешна');
      toast.success('Текст распознан');
      
      // Вызываем callback с текстом
      if (onTranscriptionComplete) {
        onTranscriptionComplete(transcribedText);
      }
      
      // Удаляем аудио после успешной транскрибации
      setAudioBlob(null);
      setAudioMimeType(null);
      setState('idle');
      
    } catch (error) {
      console.error('[SimpleVoiceRecorder] Ошибка транскрибации:', error);
      toast.error('Ошибка распознавания аудио');
      setState('idle');
    }
  };
  
  /**
   * Удалить аудио
   */
  const handleDelete = () => {
    setAudioBlob(null);
    setAudioMimeType(null);
    setState('idle');
    toast.success('Аудио удалено');
  };
  
  /**
   * Форматирование времени
   */
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  return (
    <div className="flex items-center gap-2">
      {/* Кнопка записи / остановки */}
      {!audioBlob && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (state === 'recording') {
              handleStopRecording();
            } else {
              handleStartRecording();
            }
          }}
          disabled={disabled || state === 'transcribing'}
          className={`w-8 h-8 flex items-center justify-center rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
            state === 'recording'
              ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
              : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
          title={state === 'recording' ? 'Остановить запись' : 'Записать аудио'}
        >
          <span className="text-sm">{state === 'recording' ? '🔴' : '🎤'}</span>
        </button>
      )}
      
      {/* Кнопка транскрибации */}
      {audioBlob && state !== 'transcribing' && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleTranscribe();
          }}
          disabled={disabled}
          className="w-8 h-8 flex items-center justify-center rounded-lg border border-blue-200 dark:border-blue-800 bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Отправить на транскрибацию"
        >
          <span className="text-sm">⬆️</span>
        </button>
      )}
      
      {/* Кнопка удаления */}
      {audioBlob && state !== 'transcribing' && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleDelete();
          }}
          disabled={disabled}
          className="w-8 h-8 flex items-center justify-center rounded-lg border border-red-200 dark:border-red-800 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Удалить аудио"
        >
          <span className="text-sm text-red-500">❌</span>
        </button>
      )}
      
      {/* Таймер записи */}
      {state === 'recording' && (
        <span className="text-sm text-red-500 font-mono">
          {formatTime(recordingDuration)}
        </span>
      )}
      
      {/* Индикатор транскрибации */}
      {state === 'transcribing' && (
        <span className="text-sm text-blue-500">
          ⏳ Распознавание...
        </span>
      )}
      
      {/* Индикатор записанного аудио */}
      {audioBlob && state === 'idle' && (
        <span className="text-xs text-gray-500 dark:text-gray-400">
          ✓ Готово
        </span>
      )}
    </div>
  );
}
